import { isEnterpriseDistribution, SITE } from '@/app/constants';
import type { SupportedLanguage, TaskInfo } from '@/app/types';
import { CHAR2V_TUTORIALS, IMG2V_TUTORIALS } from '../../constants';

type TutorialEmptyStateMessageKey =
  | 'task.emptyTutorial.character2video.message'
  | 'task.emptyTutorial.extension.message'
  | 'task.emptyTutorial.imageEdit.message'
  | 'task.emptyTutorial.img2video.message'
  | 'task.emptyTutorial.upscale.message'
  | 'task.emptyTutorial.upscale_image.message'
  | 'task.emptyTutorial.faceSwap.message'
  | 'task.emptyTutorial.oneClickVideoProduction.message'
  | 'task.emptyTutorial.videoEdit.message'
  | 'task.emptyTutorial.videoRemake.message'
  | 'task.emptyTutorial.musicVideo.message'
  | 'task.emptyTutorial.lipSync.message'
  | 'task.emptyTutorial.offlineAvatar.message';

type TutorialEmptyStateStepGroupKey =
  | 'extension'
  | 'imageEdit'
  | 'upscale_image'
  | 'faceSwap'
  | 'oneClickVideoProduction'
  | 'upscale'
  | 'videoEdit'
  | 'videoRemake'
  | 'musicVideo'
  | 'lipSync'
  | 'offlineAvatar';
type TutorialEmptyStateStepKey = 'step1' | 'step2' | 'step3';
type TutorialEmptyStateStepTextKey =
  `task.emptyTutorial.${TutorialEmptyStateStepGroupKey}.steps.${TutorialEmptyStateStepKey}.${'title' | 'description'}`;
type TutorialStepAssetKey =
  | 'edit'
  | 'extend'
  | 'one_click'
  | 'remake'
  | 'upscale'
  | 'face_swap'
  | 'music_video'
  | 'lip_sync'
  | 'offline_avatar';
type LocalizedAsset = Partial<Record<SupportedLanguage, string>>;

interface BaseTutorialEmptyStateConfig {
  routePrefixes: string[];
  messageKey: TutorialEmptyStateMessageKey;
}

interface ImageTutorialEmptyStateConfig extends BaseTutorialEmptyStateConfig {
  media: {
    type: 'image';
    srcByLocale: LocalizedAsset;
  };
}

interface VideoTutorialEmptyStateConfig extends BaseTutorialEmptyStateConfig {
  media: {
    type: 'video';
    srcByLocale: LocalizedAsset;
    posterByLocale?: LocalizedAsset;
  };
}

interface StepsTutorialEmptyStateConfig extends BaseTutorialEmptyStateConfig {
  media: {
    type: 'steps';
    assetKey: TutorialStepAssetKey;
    stepGroupKey: TutorialEmptyStateStepGroupKey;
  };
}

type TaskHistoryTutorialEmptyStateConfig =
  | ImageTutorialEmptyStateConfig
  | StepsTutorialEmptyStateConfig
  | VideoTutorialEmptyStateConfig;

interface TaskHistoryDefaultTaskTypeConfig {
  routePrefixes: string[];
  taskTypeKeys: TaskInfo['type'][];
}

export type ResolvedTaskHistoryTutorialEmptyState =
  | {
      type: 'image';
      messageKey: TutorialEmptyStateMessageKey;
      media: {
        src: string;
      };
    }
  | {
      type: 'steps';
      messageKey: TutorialEmptyStateMessageKey;
      steps: Array<{
        imageSrc: string;
        titleKey: TutorialEmptyStateStepTextKey;
        descriptionKey: TutorialEmptyStateStepTextKey;
      }>;
    }
  | {
      type: 'video';
      messageKey: TutorialEmptyStateMessageKey;
      media: {
        src: string;
        posterSrc?: string;
      };
    };

export interface TaskHistoryRouteContext {
  defaultTaskTypeKeys: TaskInfo['type'][];
  tutorialEmptyState: ResolvedTaskHistoryTutorialEmptyState | null;
  ttsOnlyHistory: boolean;
}

const TASK_HISTORY_DEFAULT_TASK_TYPE_CONFIGS: TaskHistoryDefaultTaskTypeConfig[] = [
  {
    routePrefixes: ['/create/reference2image', '/create/text2image'],
    taskTypeKeys: ['reference2image', 'text2image'],
  },
  {
    routePrefixes: ['/create/extension'],
    taskTypeKeys: ['extend'],
  },
  {
    routePrefixes: ['/create/image-edit'],
    taskTypeKeys: ['image_edit'],
  },
  {
    routePrefixes: ['/create/video-edit'],
    taskTypeKeys: ['video_edit'],
  },
  {
    routePrefixes: ['/create/upscale-image'],
    taskTypeKeys: ['upscale_image'],
  },
  {
    routePrefixes: ['/create/upscale-video'],
    taskTypeKeys: ['upscale'],
  },
  {
    routePrefixes: ['/create/face-swap'],
    taskTypeKeys: ['face_swap'],
  },
  {
    routePrefixes: ['/create/one-click-video-production'],
    taskTypeKeys: ['one_click'],
  },
  {
    routePrefixes: ['/create/video-remake'],
    taskTypeKeys: ['remake'],
  },
  {
    routePrefixes: ['/create/one-click-mv'],
    taskTypeKeys: ['music_video'],
  },
  {
    routePrefixes: ['/create/lip-sync'],
    taskTypeKeys: ['lip_sync'],
  },
  {
    routePrefixes: ['/create/ai-avatar-generator'],
    taskTypeKeys: ['offline_avatar'],
  },
  {
    routePrefixes: ['/create/story-grid'],
    taskTypeKeys: ['short_story'],
  },
  {
    routePrefixes: ['/create/style-transfer'],
    taskTypeKeys: ['style_transfer'],
  },
  {
    routePrefixes: ['/create/motion-control'],
    taskTypeKeys: ['motion_control'],
  },
];

const TASK_HISTORY_TUTORIAL_STEP_ASSETS: Record<
  TutorialStepAssetKey,
  {
    chinaCn: readonly [string, string, string];
    overseasCn: readonly [string, string, string];
    overseasEn: readonly [string, string, string];
  }
> = {
  edit: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/edit_1_cn-7ca4a144.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/edit_2_cn-01280c3f.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/edit_3_cn-df2b49ff.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/edit_1_cn-7ca4a144.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/edit_2_cn-01280c3f.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/edit_3_cn-df2b49ff.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/edit_1_en-b9bc11a5.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/edit_2_en-a7d9a09a.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/edit_3_en-df2b49ff.webp',
    ],
  },
  extend: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/extend_1_cn-8487a3d1.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/extend_2_cn-208f56c6.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/extend_3_cn-0623104c.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/extend_1_cn-8487a3d1.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/extend_2_cn-208f56c6.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/extend_3_cn-0623104c.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/extend_1_en-8ff6e637.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/extend_2_en-06c887e8.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/extend_3_en-0623104c.webp',
    ],
  },
  one_click: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/one_click_1_cn-bb8393d3.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/one_click_2_cn-ddbbf824.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/one_click_3_cn-582abfdd.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/one_click_1_cn-bb8393d3.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/one_click_2_cn-ddbbf824.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/one_click_3_cn-582abfdd.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/one_click_1_en-24302c4c.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/one_click_2_en-b273a2a6.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/one_click_3_en-582abfdd.webp',
    ],
  },
  remake: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/remake_1_cn-b30c8694.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/remake_2_cn-efbe55c3.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/remake_3_cn-e8de83fe.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/remake_1_cn-b30c8694.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/remake_2_cn-efbe55c3.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/remake_3_cn-e8de83fe.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/remake_1_en-3b242937.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/remake_2_en-e2326aad.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/remake_3_en-e8de83fe.webp',
    ],
  },
  upscale: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/upscale_1_cn-ebf05325.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/upscale_2_cn-d151355f.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/upscale_3_cn-7e40e716.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/upscale_1_cn-ebf05325.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/upscale_2_cn-d151355f.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/upscale_3_cn-7e40e716.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/upscale_1_en-1ae92dcf.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/upscale_2_en-ac0e2a25.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/upscale_3_en-7e40e716.webp',
    ],
  },
  face_swap: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/face_1_cn-501a3b9d.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/face_2_cn-5492ed9a.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/face_3_cn-3920c8aa.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/face_1_cn-501a3b9d.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/face_2_cn-5492ed9a.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/face_3_cn-3920c8aa.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/face_1_en-7f5816ef.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/face_2_en-3de1fba1.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/face_3_en-3920c8aa.webp',
    ],
  },
  music_video: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/music_video_1_cn-d4e691ad.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/music_video_2_cn-3e4c9eed.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/music_video_3_cn-5849c56b.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/music_video_1_cn-d4e691ad.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/music_video_2_cn-3e4c9eed.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/music_video_3_cn-5849c56b.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/music_video_1_en-fc79cc8a.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/music_video_2_en-37795b3d.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/music_video_3_en-5849c56b.webp',
    ],
  },
  lip_sync: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/lipsync1-bc1cd1d1.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/lipsync2-6ca94c69.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/lipsync3-8b9f4f85.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync1-bc1cd1d1.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync2-6ca94c69.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync3-8b9f4f85.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync-en1-f1c96500.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync-en2-01c24152.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync3-8b9f4f85.webp',
    ],
  },
  offline_avatar: {
    chinaCn: [
      'https://image01-aliyun.vidu.zone/vidu/media-asset/lipsync1-bc1cd1d1.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/lipsync2-6ca94c69.webp',
      'https://image01-aliyun.vidu.zone/vidu/media-asset/lipsync3-8b9f4f85.webp',
    ],
    overseasCn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync1-bc1cd1d1.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync2-6ca94c69.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync3-8b9f4f85.webp',
    ],
    overseasEn: [
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync-en1-f1c96500.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync-en2-01c24152.webp',
      'https://image01.cf.vidu.studio/vidu/media-asset/lipsync3-8b9f4f85.webp',
    ],
  },
};

const TASK_HISTORY_TUTORIAL_EMPTY_STATES: TaskHistoryTutorialEmptyStateConfig[] = [
  {
    routePrefixes: ['/create/character2video'],
    messageKey: 'task.emptyTutorial.character2video.message',
    media: {
      type: 'video',
      srcByLocale: CHAR2V_TUTORIALS[0].videoUrl,
      posterByLocale: CHAR2V_TUTORIALS[0].posterUrl,
    },
  },
  {
    routePrefixes: ['/create/img2video'],
    messageKey: 'task.emptyTutorial.img2video.message',
    media: {
      type: 'video',
      srcByLocale: IMG2V_TUTORIALS[0].videoUrl,
      posterByLocale: IMG2V_TUTORIALS[0].posterUrl,
    },
  },
  {
    routePrefixes: ['/create/extension'],
    messageKey: 'task.emptyTutorial.extension.message',
    media: {
      type: 'steps',
      assetKey: 'extend',
      stepGroupKey: 'extension',
    },
  },
  {
    routePrefixes: ['/create/image-edit'],
    messageKey: 'task.emptyTutorial.imageEdit.message',
    media: {
      type: 'steps',
      assetKey: 'edit',
      stepGroupKey: 'imageEdit',
    },
  },
  {
    routePrefixes: ['/create/upscale-image'],
    messageKey: 'task.emptyTutorial.upscale_image.message',
    media: {
      type: 'steps',
      assetKey: 'upscale',
      stepGroupKey: 'upscale_image',
    },
  },
  {
    routePrefixes: ['/create/upscale-video'],
    messageKey: 'task.emptyTutorial.upscale.message',
    media: {
      type: 'steps',
      assetKey: 'upscale',
      stepGroupKey: 'upscale',
    },
  },
  {
    routePrefixes: ['/create/face-swap'],
    messageKey: 'task.emptyTutorial.faceSwap.message',
    media: {
      type: 'steps',
      assetKey: 'face_swap',
      stepGroupKey: 'faceSwap',
    },
  },
  {
    routePrefixes: ['/create/one-click-video-production'],
    messageKey: 'task.emptyTutorial.oneClickVideoProduction.message',
    media: {
      type: 'steps',
      assetKey: 'one_click',
      stepGroupKey: 'oneClickVideoProduction',
    },
  },
  {
    routePrefixes: ['/create/video-edit'],
    messageKey: 'task.emptyTutorial.videoEdit.message',
    media: {
      type: 'steps',
      assetKey: 'edit',
      stepGroupKey: 'videoEdit',
    },
  },
  {
    routePrefixes: ['/create/video-remake'],
    messageKey: 'task.emptyTutorial.videoRemake.message',
    media: {
      type: 'steps',
      assetKey: 'remake',
      stepGroupKey: 'videoRemake',
    },
  },
  {
    routePrefixes: ['/create/one-click-mv'],
    messageKey: 'task.emptyTutorial.musicVideo.message',
    media: {
      type: 'steps',
      assetKey: 'music_video',
      stepGroupKey: 'musicVideo',
    },
  },
  {
    routePrefixes: ['/create/lip-sync'],
    messageKey: 'task.emptyTutorial.lipSync.message',
    media: {
      type: 'steps',
      assetKey: 'lip_sync',
      stepGroupKey: 'lipSync',
    },
  },
  {
    routePrefixes: ['/create/ai-avatar-generator'],
    messageKey: 'task.emptyTutorial.offlineAvatar.message',
    media: {
      type: 'steps',
      assetKey: 'offline_avatar',
      stepGroupKey: 'offlineAvatar',
    },
  },
];

function getLocalizedAssetUrl(asset: LocalizedAsset | undefined, locale: SupportedLanguage): string | undefined {
  if (!asset) return undefined;

  return asset[locale] ?? asset.en;
}

function getTutorialStepImageUrls(
  assetKey: TutorialStepAssetKey,
  locale: SupportedLanguage,
): readonly [string, string, string] {
  const assetGroup = TASK_HISTORY_TUTORIAL_STEP_ASSETS[assetKey];
  if (SITE === 'china') {
    return assetGroup.chinaCn;
  }

  return locale === 'zh' ? assetGroup.overseasCn : assetGroup.overseasEn;
}

function getTutorialStepTextKey(
  stepGroupKey: TutorialEmptyStateStepGroupKey,
  stepKey: TutorialEmptyStateStepKey,
  field: 'description' | 'title',
): TutorialEmptyStateStepTextKey {
  return `task.emptyTutorial.${stepGroupKey}.steps.${stepKey}.${field}`;
}

export function getTaskHistoryDefaultTaskTypeKeys(pathname: string): TaskInfo['type'][] {
  const matchedConfig = TASK_HISTORY_DEFAULT_TASK_TYPE_CONFIGS.find((config) =>
    config.routePrefixes.some((routePrefix) => pathname.includes(routePrefix)),
  );

  return matchedConfig?.taskTypeKeys ?? [];
}

export function getTaskHistoryTutorialEmptyState(
  pathname: string,
  locale: SupportedLanguage,
): ResolvedTaskHistoryTutorialEmptyState | null {
  const matchedConfig = TASK_HISTORY_TUTORIAL_EMPTY_STATES.find((config) =>
    config.routePrefixes.some((routePrefix) => pathname.includes(routePrefix)),
  );

  if (isEnterpriseDistribution || !matchedConfig) {
    return null;
  }

  if (matchedConfig.media.type === 'steps') {
    const { assetKey, stepGroupKey } = matchedConfig.media;
    const imageUrls = getTutorialStepImageUrls(assetKey, locale);
    const stepKeys: TutorialEmptyStateStepKey[] = ['step1', 'step2', 'step3'];

    return {
      type: 'steps',
      messageKey: matchedConfig.messageKey,
      steps: stepKeys.map((stepKey, index) => ({
        imageSrc: imageUrls[index],
        titleKey: getTutorialStepTextKey(stepGroupKey, stepKey, 'title'),
        descriptionKey: getTutorialStepTextKey(stepGroupKey, stepKey, 'description'),
      })),
    };
  }

  if (matchedConfig.media.type === 'image') {
    const src = getLocalizedAssetUrl(matchedConfig.media.srcByLocale, locale);
    if (!src) {
      return null;
    }

    return {
      type: 'image',
      messageKey: matchedConfig.messageKey,
      media: {
        src,
      },
    };
  }

  const src = getLocalizedAssetUrl(matchedConfig.media.srcByLocale, locale);
  if (!src) {
    return null;
  }

  return {
    type: 'video',
    messageKey: matchedConfig.messageKey,
    media: {
      src,
      posterSrc: getLocalizedAssetUrl(matchedConfig.media.posterByLocale, locale),
    },
  };
}

export function getTaskHistoryRouteContext(pathname: string, locale: SupportedLanguage): TaskHistoryRouteContext {
  return {
    defaultTaskTypeKeys: getTaskHistoryDefaultTaskTypeKeys(pathname),
    tutorialEmptyState: getTaskHistoryTutorialEmptyState(pathname, locale),
    ttsOnlyHistory: pathname.includes('/create/text-to-speech'),
  };
}
